<template>
  <div class="ruleBorder ml-3 my-1">
    {{ displayString }}
    <div v-if="selection.subSelections && selection.subSelections.length">
      <div
        v-for="(subSelection, index) in selection.subSelections"
        :key="index"
        class="ml-1"
      >
        <SimpleRequisiteDataSelectDisplay :selection="subSelection" :titleInterpolation="titleInterpolation" />
      </div>
    </div>
    <template v-else-if="selection.values && selection.values.length">
      <div v-if="isLoading" class="d-flex align-items-center pl-2">
        <AppSpinner small />
        <span class="ml-2">Loading...</span>
      </div>
      <ul
        v-else
        class="m-0"
      >
        <li
          v-for="(value, valueIndex) in getNormalizedValues(selection.values)"
          :key="valueIndex"
          class="m-0 p-0"
          :data-test="`requisite_courses_list_item_${valueIndex}`"
        >
          <span v-if="selection.condition === 'courses'">
            <span
              class="mr-2 flex-1"
              :class="{
                'diff-change-removed': removed && removedRuleObjectIds.includes(value.value[0]),
                'diff-change-added': added && addedRuleObjectIds.includes(value.value[0]),
              }"
              v-html="value.value.map(getCourseLabel).join(` </br><strong>${value.logic ? value.logic.toUpperCase(): ''}</strong> `)"
            />
          </span>
          <span v-else-if="selection.condition === 'programs'">
            <span
              class="mr-2 flex-1"
              :class="{
                'diff-change-removed': removed && removedRuleObjectIds.includes(value.value[0]),
                'diff-change-added': added && addedRuleObjectIds.includes(value.value[0]),
              }"
              v-html="value.value.map(getProgramLabel).join(` </br><strong>${value.logic ? value.logic.toUpperCase() : ''}</strong> `)"
            />
          </span>
          <span v-else-if="selection.condition === 'courseSets'">
            <span
              class="mr-2 flex-1"
              :class="{
                'diff-change-removed': removed && removedRuleObjectIds.includes(value.value[0]),
                'diff-change-added': added && addedRuleObjectIds.includes(value.value[0]),
              }"
              v-html="value.value.map(getCourseSetLabel).join(` </br><strong>${value.logic ? value.logic.toUpperCase() : ''}</strong> `)"
            />
          </span>
          <span v-else-if="selection.condition === 'requisiteSets'">
            <span
              class="mr-2 flex-1"
              :class="{
                'diff-change-removed': removed && removedRuleObjectIds.includes(value.value[0]),
                'diff-change-added': added && addedRuleObjectIds.includes(value.value[0]),
              }"
              v-html="value.value.map(getRequisiteSetLabel).join(` </br><strong>${value.logic ? value.logic.toUpperCase() : ''}</strong> `)"
            />
          </span>

          <span
            v-else
            :class="{
              'diff-change-removed': removed && removedRuleObjectIds.includes(value.value[0]),
              'diff-change-added': added && addedRuleObjectIds.includes(value.value[0]),
            }"
          >
            {{ value }}
          </span>
        </li>
      </ul>
    </template>
    <div
      v-else
      class="bg-light rounded-5 p-2 text-center border ml-2"
    >No selection provided</div>
  </div>
</template>

<script>
import { inject, computed, watch, isRef } from '@vue/composition-api'
import { mapGetters } from 'vuex'
import store from '@/store'
import useCourse from '@cm/client/composables/useCourse'
import useProgram from '@cm/client/composables/useProgram'
import { requisiteUtils } from '@coursedog/requisites'
import useSharedCourses from '@/components/composables/useSharedCourses'
import useSharedPrograms from '@/components/composables/useSharedPrograms'
import useSchoolSettings from '@/components/composables/useSchoolSettings'

const reqUtil = requisiteUtils()

export default {
  name: 'SimpleRequisiteDataSelectDisplay',
  components: {},
  props: {
    selection: {
      type: Object,
      default: () => ({}),
    },
    added: {
      type: Boolean,
      default: false,
    },
    removed: {
      type: Boolean,
      default: false,
    },
    addedRuleObjectIds: {
      type: Array,
      default: () => ([]),
    },
    removedRuleObjectIds: {
      type: Array,
      default: () => ([]),
    },
    titleInterpolation: {
      type: String,
      default: '',
    },
  },
  setup (props) {
    const effectiveDatesRange = inject('effectiveDatesRange', '')
    const { fetchCourse, getCourseByDatesRangeLookup, isLoading: isLoadingCourses } = useSharedCourses()
    const { fetchProgram, getProgramByDatesRangeLookup, isLoading: isLoadingPrograms } = useSharedPrograms()
    const { schoolSettings, getSettings, settingsLoaded } = useSchoolSettings()

    // fetch settings if settings are not already loaded
    if (!settingsLoaded.value) getSettings()

    const isLoading = computed(() => isLoadingCourses.value || isLoadingPrograms.value)

    const coursesInRequsites = props.selection.condition === 'courses'
      ? props.selection.values.map(value => ({ courseGroupId: value.value }))
      : []
    const programsInRequsites = props.selection.condition === 'programs'
      ? props.selection.values.map(value => ({ programGroupId: value }))
      : []
    const courseSetsInRequsites = props.selection.condition === 'courseSets'
      ? props.selection.values.map(value => ({ id: value.value }))
      : []

    const requisiteSetsInRequisites = props.selection.condition === 'requisiteSets'
      ? props.selection.values.map(value => ({ id: value.value }))
      : []

    function fetchSelections () {
      const useFetchRequisitesWithLatestRevision = schoolSettings.value?.enableFetchingRequisitesWithLatestRevisions
      coursesInRequsites.forEach(c => fetchCourse({
        effectiveDatesRange: isRef(effectiveDatesRange) ? effectiveDatesRange.value : effectiveDatesRange,
        courseGroupId: c.courseGroupId,
        columns: [ 'code', 'longName', 'name', 'subjectCode', 'courseNumber', 'requestStatus', 'status' ],
        limit: 2000,
        useFetchingRequisitesLatestRevisionSettings: useFetchRequisitesWithLatestRevision,
        ...(props.titleInterpolation ? { titleInterpolation: props.titleInterpolation } : {}),
      }))
      programsInRequsites.forEach(p => fetchProgram({
        effectiveDatesRange: isRef(effectiveDatesRange) ? effectiveDatesRange.value : effectiveDatesRange,
        programGroupId: p.programGroupId,
        columns: [ 'code', 'longName', 'name', 'transcriptDescription', 'requestStatus', 'status' ],
        useFetchingRequisitesLatestRevisionSettings: useFetchRequisitesWithLatestRevision,
      }))
      store.dispatch('curriculumCourses/loadCourseSets', {
        list: courseSetsInRequsites.flatMap(data => data.id).join(','),
        returnFields: [ '_id', 'id', 'deprecatedId', 'name' ],
      })
      store.dispatch('curriculumPrograms/loadRequisiteSets', {
        list: requisiteSetsInRequisites.flatMap(data => data.id).join(','),
        effectiveDatesRange: isRef(effectiveDatesRange) ? effectiveDatesRange.value : effectiveDatesRange,
      })
    }

    // ensure we only load requisites once settingsLoaded have loaded.
    // If settings have not loaded, we should not fetch data, as
    // we need to know if enableFetchingRequisitesWithLatestRevisions is true or false
    watch(settingsLoaded, (val) => { if (val) fetchSelections() }, { immediate: true })

    const {
      generateCourseLabel,
      generateCourseStatusLabel,
    } = useCourse()
    const {
      generateProgramLabel,
    } = useProgram()
    return {
      generateCourseLabel,
      generateCourseStatusLabel,
      generateProgramLabel,
      effectiveDatesRange,
      getCourseByDatesRangeLookup,
      getProgramByDatesRangeLookup,
      schoolSettings,
      isLoading,
      settingsLoaded,
      fetchSelections,
    }
  },
  computed: {
    ...mapGetters('curriculumCourses', [ 'courseSetById' ]),
    ...mapGetters('curriculumPrograms', [ 'requisiteSetById' ]),
    displayString () {
      return reqUtil.simpleRequisiteDataSelectDisplayString(this.selection)
    },
    effectiveDates () {
      return (this.effectiveDatesRange || '').split(',').map(v => v || undefined)
    },
  },
  mounted () {
    if (this.settingsLoaded) { this.fetchSelections() }
  },
  methods: {
    getNormalizedValues: reqUtil.getNormalizedValues,
    getCourseLabel (courseGroupId) {
      const useFetchRequisitesWithLatestRevision = this.schoolSettings?.enableFetchingRequisitesWithLatestRevisions
      // the following setting, if enabled, will display the ID of the course in the UI if the
      // ID cannot be matched to a course. If disabled, we will display "course not found"
      const displayRequisiteIdInlineIfEntityNotFound = this.schoolSettings?.displayRequisiteIdInlineIfEntityNotFound
      const course = this.getCourseByDatesRangeLookup({
        courseGroupId: { value: courseGroupId },
        effectiveDatesRange: { value: this.effectiveDatesRange },
        useFetchRequisitesWithLatestRevision,
        mergeDuplicateCourses: true,
      })

      let courseLabel = course?.globalCourseTitle || this.generateCourseLabel(course, displayRequisiteIdInlineIfEntityNotFound ? courseGroupId : null)
      const courseStatusLabel = this.generateCourseStatusLabel(course)

      if (course?.globalCourseTitle && courseStatusLabel) { courseLabel = `${courseLabel} (${courseStatusLabel})` }

      // if router and course exists, create a link for the course that the user can click.
      // Otherwise do not create link and display label as text
      if (this.$router && course) {
        return `<a href="${this.$router.resolve({
          name: 'cmCourseByCourseGroupId',
          params: { courseGroupId, effectiveStartDate: this.effectiveDates[0], effectiveEndDate: this.effectiveDates[1] },
          query: {
            useLatestRevision: this.schoolSettings?.enableFetchingRequisitesWithLatestRevisions,
          },
        }).href}">${courseLabel}</a>`
      }
      return courseLabel
    },
    // TODO: configure program labels to support displayRequisiteIdInlineIfEntityNotFound feature flags
    getProgramLabel (programGroupId) {
      const useFetchRequisitesWithLatestRevision = this.schoolSettings?.enableFetchingRequisitesWithLatestRevisions
      const program = this.getProgramByDatesRangeLookup({
        programGroupId: { value: programGroupId },
        effectiveDatesRange: { value: this.effectiveDatesRange },
        useFetchRequisitesWithLatestRevision,
      })

      if (this.$router && program) {
        return `<a href="${this.$router.resolve({
          name: 'cmProgramByProgramGroupId',
          params: { programGroupId, effectiveStartDate: this.effectiveDates[0], effectiveEndDate: this.effectiveDates[1] },
          query: {
            useLatestRevision: useFetchRequisitesWithLatestRevision,
          },
        }).href}">${this.generateProgramLabel(program)}</a>`
      }
      return this.generateProgramLabel(program)
    },
    getCourseSetLabel (courseSetId) {
      const courseSet = this.courseSetById(courseSetId) || {}

      if (this.$router && courseSet) {
        return `<a href="${this.$router.resolve({ name: 'cmCourseSets' }).href}">${courseSet.name || 'Untitled Course Set'}</a>`
      }

      return courseSet.name || 'Untitled Course Set'
    },
    getRequisiteSetLabel (requisiteSetId) {
      const requisiteSet = this.requisiteSetById(requisiteSetId) || {}

      if (this.$router) {
        return `<a href="${this.$router.resolve({ name: 'cmRequisiteSet', params: { requisiteSetId: requisiteSet._id } }).href}">${requisiteSet.name || 'Untitled Requirement Set'}</a>`
      }

      return requisiteSet.name || 'Untitled Requirement Set'
    },

    getRequisiteSet (requisiteSetId) {
      const requisiteSet = this.requisiteSetById(requisiteSetId) || {}
      return requisiteSet?.requisites || null
    },

  },
}
</script>

<style scoped>
  .ruleBorder {
    border-left: 1px solid var(--dark-gray) !important;
  }

  .diff-change-removed {
    background: #fccac7;
    color: #7f231c;
  }

  .diff-change-added {
    background: #cde9ce !important;
    color: #285b2a;
  }

</style>
